import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { BarChart3, RefreshCw } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

const fmt = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

export default function AffiliateFunnel() {
  const [days, setDays] = useState(30);
  const [site, setSite] = useState(null);
  const [byAff, setByAff] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get(`/admin/aff-analytics/funnel?days=${days}`),
        api.get(`/admin/aff-analytics/funnel-by-affiliate?days=${days}&limit=30`),
      ]);
      setSite(s.data);
      setByAff(a.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load funnel");
    } finally {
      setLoading(false);
    }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const stages = site?.stages || [];

  return (
    <div data-testid="admin-funnel-page">
      <PageHeader
        eyebrow="ANALYTICS"
        title="Conversion Funnel"
        subtitle="Site-wide Click → Registration → FTD → Deposit → Net Revenue."
        right={
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="h-10 px-3 rounded-lg bg-[#0f1a2e] border border-white/10 text-white text-[13px]"
              data-testid="admin-funnel-window"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button onClick={load} className="px-3 h-10 rounded-lg bg-white/5 border border-white/10 text-white text-[13px] inline-flex items-center gap-1.5">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="rounded-xl border border-white/10 p-10 text-center text-white/55">Loading…</div>
      ) : !site ? (
        <div className="rounded-xl border border-white/10 p-10 text-center text-white/55">No data</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {stages.map((s, i) => (
              <div key={s.name} className="rounded-xl p-4 bg-[#0f1a2e] border border-white/10">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/55">{s.name}</div>
                <div className="mt-2 text-2xl font-black text-white">{fmt(s.value)}</div>
                <div className="text-[10.5px] text-white/45 mt-1">
                  {i === 0 ? "Top of funnel" : <><span className="text-emerald-400 font-bold">{s.pct_of_prev}%</span> of prev</>}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl p-5 bg-[#0f1a2e] border border-white/10 mb-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-300 mb-3">Funnel ({site.window_days}d)</div>
            <div style={{ width: "100%", height: 320, minHeight: 280 }}>
              <ResponsiveContainer>
                <BarChart data={stages.map((s, i) => ({ ...s, idx: i }))}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                  <XAxis dataKey="name" stroke="#9ca3af" fontSize={11} />
                  <YAxis stroke="#9ca3af" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#0a070f", border: "1px solid rgba(34,192,115,0.4)", borderRadius: 8 }} formatter={(v) => fmt(v)} />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {stages.map((_, i) => <Cell key={i} fill={["#22c073", "#34d399", "#d4af37", "#fbbf24", "#f59e0b"][i % 5]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Per-affiliate breakdown */}
          <div className="rounded-xl bg-[#0f1a2e] border border-white/10 overflow-hidden">
            <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
              <BarChart3 size={14} className="text-emerald-300" />
              <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-bold">Per-Affiliate Funnel</div>
            </div>
            <table className="w-full text-[12.5px]">
              <thead><tr className="bg-white/[0.04] text-emerald-300">
                <th className="text-left p-3">Affiliate</th>
                <th className="text-left p-3">Promo</th>
                <th className="text-right p-3">Clicks</th>
                <th className="text-right p-3">Registrations</th>
                <th className="text-right p-3">FTD</th>
                <th className="text-right p-3">Deposits</th>
                <th className="text-right p-3">Net Revenue</th>
              </tr></thead>
              <tbody>
                {byAff.length === 0 ? (
                  <tr><td colSpan={7} className="p-6 text-center text-white/55">No data</td></tr>
                ) : byAff.map((a) => (
                  <tr key={a.user_id} className="border-t border-white/5">
                    <td className="p-3 text-white">{a.username || "—"}</td>
                    <td className="p-3"><code className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 text-[11px]">{a.promo_code || "—"}</code></td>
                    <td className="p-3 text-right text-white">{fmt(a.clicks)}</td>
                    <td className="p-3 text-right text-white">{fmt(a.registrations)}</td>
                    <td className="p-3 text-right text-white">{fmt(a.ftd)}</td>
                    <td className="p-3 text-right text-white">৳{fmt(a.deposits)}</td>
                    <td className="p-3 text-right font-black" style={{ color: "#d4af37" }}>৳{fmt(a.net_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
